'use client'

import { useState, useEffect, ReactNode } from 'react'
import { useRouter } from '@bprogress/next/app'
import { ScrollArea } from '@/components/ui/scroll-area'
import { AppBreadcrumb, AppFooter, AppHeader, AppSidebar } from '@/components/partials/layout'
import ScrollToTop from '@/components/partials/layout/scroll-to-top'
import { WebSocketProvider } from '@/providers'
import { MetaData } from '@/types/meta'
import useMetaStore from '@/store/meta'
import { useLocalStorage, useTokenRefresh } from '@/hooks'
import { setRouter } from '@/lib/router'

type PropsType = {
  children: ReactNode
  metaData: MetaData
  locale: string
}

const AppLayoutClient = ({ children, metaData, locale }: PropsType) => {
  const router = useRouter()
  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage('sidebarCollapsed', false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [mounted, setMounted] = useState(false)
  const { setMeta, metaState } = useMetaStore()

  // Synchronously hydrate the meta-store so child components see it on their first render
  // (useEffect would only run after all children have mounted, causing a brief null state)
  useState(() => {
    if (metaData && !useMetaStore.getState().metaState.response) {
      useMetaStore.getState().setMeta(metaData)
    }
  })

  useTokenRefresh()

  // Use meta from store if available, otherwise use props
  const currentMetaData = metaState.response || metaData

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    setMounted(true)

    // Remove the blocking init CSS injected before React hydration.
    // It used !important to prevent flash, but must be removed so React can
    // freely control sidebar/content widths after hydration.
    const initStyle = document.getElementById('sidebar-init-css')
    if (initStyle) {
      initStyle.remove()
    }

    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && sidebarOpen) {
        setSidebarOpen(false)
      }
    }

    window.addEventListener('keydown', handleEscKey)
    return () => window.removeEventListener('keydown', handleEscKey)
  }, [sidebarOpen])

  useEffect(() => {
    setRouter(router)
  }, [router])

  useEffect(() => {
    if (metaData) {
      setMeta(metaData)
    }
  }, [metaData, setMeta])

  return (
    <WebSocketProvider>
      <div className="h-screen bg-transparent">
        <div className="flex h-full bg-transparent">
          <AppSidebar
            collapsed={sidebarCollapsed}
            open={sidebarOpen}
            onOpenChange={setSidebarOpen}
            isMobile={isMobile}
            metaData={currentMetaData}
            locale={locale}
            disableTransition={!mounted}
          />

          {isMobile && sidebarOpen && (
            <div className="fixed inset-0 z-30 bg-black/50 md:hidden" onClick={() => setSidebarOpen(false)} />
          )}

          <div
            suppressHydrationWarning
            className={`app-content-init flex h-full flex-1 flex-col bg-transparent ${mounted ? 'transition-all duration-300' : ''} ${
              isMobile ? 'ml-0' : sidebarCollapsed ? 'ml-16' : 'ml-72'
            }`}>
            <AppHeader
              onMenuClick={() => setSidebarOpen(!sidebarOpen)}
              onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
              isMobile={isMobile}
              sidebarCollapsed={sidebarCollapsed}
              metaData={currentMetaData}
              locale={locale}
            />

            <AppBreadcrumb />

            <ScrollArea id="main-scroll-area" className="h-full bg-transparent">
              <main className="p-6">{children}</main>
            </ScrollArea>

            <div className="relative shrink-0">
              <ScrollToTop />
              <AppFooter metaData={metaData} locale={locale} />
            </div>
          </div>
        </div>
      </div>
    </WebSocketProvider>
  )
}

export default AppLayoutClient
